import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { apiFetch } from "../utils/api";
import { showToast } from "../toastService";
import Header from "../components/Header";
import BottomNavbar from "../components/BottomNavbar";
import "./css/ContractorDetails.css";
import noWorkImage from "../assets/images/no_work.png";

export default function ContractorDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [contractor, setContractor] = useState(null);
  const [pastWorks, setPastWorks] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [error, setError] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [updatingFavorite, setUpdatingFavorite] = useState(false);
  const [showDescriptionFull, setShowDescriptionFull] = useState(false);
  const [showPastWorkModal, setShowPastWorkModal] = useState(false);
  const [selectedPastWork, setSelectedPastWork] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showMoreServices, setShowMoreServices] = useState(false);
  const [showMoreReviews, setShowMoreReviews] = useState(false);
  const [lang] = useState(localStorage.getItem("app_language") || "en");
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const autoRotateIntervalRef = useRef(null);

  const USER_BASE_URL = process.env.REACT_APP_USER_BASE_URL;
  const VERSION = process.env.REACT_APP_VERSION;

  // Translations
  const t = {
    en: {
      loading: "Loading",
      contractorDetails: "Contractor Details",
      basicDetails: "Basic Details",
      servicesOffered: "Services Offered",
      specialisations: "Specialisations",
      pastWorks: "Past Works",
      reviews: "Reviews",
      startDate: "Start Date",
      yearsInBusiness: "Years in business",
      ratings: "Ratings",
      starRating: "Star Rating",
      description: "Description",
      readMore: "Read More",
      readLess: "Read Less",
      showMore: "Show More",
      showLess: "Show Less",
      viewDetails: "View Details",
      budget: "Budget",
      endDate: "End Date",
      workDocuments: "Work Documents",
      close: "Close",
      noContractorFound: "Contractor not found",
      errorLoading: "Failed to load contractor details",
      goToContractors: "Go to Contractors",
      noPastWorks: "No past works available",
      noReviews: "No reviews available",
    },
    ar: {
      loading: "جاري التحميل",
      contractorDetails: "تفاصيل المقاول",
      basicDetails: "التفاصيل الأساسية",
      servicesOffered: "الخدمات المقدمة",
      specialisations: "التخصصات",
      pastWorks: "الأعمال السابقة",
      reviews: "المراجعات",
      startDate: "تاريخ البدء",
      yearsInBusiness: "سنوات في العمل",
      ratings: "التقييمات",
      starRating: "تقييم النجوم",
      description: "الوصف",
      readMore: "اقرأ المزيد",
      readLess: "اقرأ أقل",
      showMore: "عرض المزيد",
      showLess: "عرض أقل",
      viewDetails: "عرض التفاصيل",
      budget: "الميزانية",
      endDate: "تاريخ الانتهاء",
      workDocuments: "مستندات العمل",
      close: "إغلاق",
      noContractorFound: "المقاول غير موجود",
      errorLoading: "فشل تحميل تفاصيل المقاول",
      goToContractors: "الذهاب إلى المقاولين",
      noPastWorks: "لا توجد أعمال سابقة متاحة",
      noReviews: "لا توجد مراجعات متاحة",
    },
  }[lang];

  // Fetch contractor details
  useEffect(() => {
    const fetchContractorDetails = async () => {
      if (!id) {
        setError(true);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(false);

        const data = await apiFetch(
          `${USER_BASE_URL}/${VERSION}/contractor/`,
          "POST",
          JSON.stringify({ id }),
          {
            "Content-Type": "application/json",
          }
        );

        if (data && data.response_code === 200 && data.response) {
          setContractor(data.response.contractor || null);
          setPastWorks(data.response.past_works || []);
          setReviews(data.response.latest_reviews || []);
          setIsFavorite(data.response.contractor?.is_favorite || false);
        } else {
          setError(true);
        }
      } catch (err) {
        console.error("Error fetching contractor details:", err);
        setError(true);
        showToast(err.message || t.errorLoading, "error");
      } finally {
        setLoading(false);
      }
    };

    fetchContractorDetails();
  }, [id, USER_BASE_URL, VERSION, t.errorLoading]);

  // Calculate years in business
  const calculateYearsInBusiness = () => {
    if (!contractor?.company_start_date) return null;
    const startDate = new Date(contractor.company_start_date);
    const currentDate = new Date();
    const diffTime = Math.abs(currentDate - startDate);
    const diffYears = Math.floor(diffTime / (1000 * 60 * 60 * 24 * 365));
    return diffYears;
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Format relative time
  const formatRelativeTime = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const diffMonths = Math.floor(diffDays / 30);
    const diffYears = Math.floor(diffDays / 365);

    const tRelative = {
      en: {
        year: "year",
        years: "years",
        month: "month",
        months: "months",
        day: "day",
        days: "days",
        ago: "ago",
        today: "today",
      },
      ar: {
        year: "سنة",
        years: "سنوات",
        month: "شهر",
        months: "أشهر",
        day: "يوم",
        days: "أيام",
        ago: "منذ",
        today: "اليوم",
      },
    }[lang];

    if (diffYears > 0) {
      return `${diffYears} ${diffYears === 1 ? tRelative.year : tRelative.years} ${tRelative.ago}`;
    } else if (diffMonths > 0) {
      return `${diffMonths} ${diffMonths === 1 ? tRelative.month : tRelative.months} ${tRelative.ago}`;
    } else if (diffDays > 0) {
      return `${diffDays} ${diffDays === 1 ? tRelative.day : tRelative.days} ${tRelative.ago}`;
    } else {
      return tRelative.today;
    }
  };

  // Get formatted categories (subcategory - category format)
  const getFormattedCategories = () => {
    if (!contractor?.sub_categories || contractor.sub_categories.length === 0) return [];
    return contractor.sub_categories.map((sc) => {
      if (typeof sc === "object" && sc.category_id && typeof sc.category_id === "object") {
        return `${sc.name} - ${sc.category_id.name}`;
      }
      return typeof sc === "object" ? sc.name : sc;
    });
  };

  // Get formatted specialisations
  const getFormattedSpecialisations = () => {
    if (!contractor?.specialisations || contractor.specialisations.length === 0) return [];
    return contractor.specialisations.map((spec) =>
      typeof spec === "object" ? spec.name : spec
    );
  };

  // Render stars
  const renderStars = (rating) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    return (
      <div className="rating-stars">
        {[...Array(fullStars)].map((_, i) => (
          <i key={i} className="bx bxs-star" style={{ color: "#f59e0b" }}></i>
        ))}
        {hasHalfStar && (
          <i className="bx bxs-star-half" style={{ color: "#f59e0b" }}></i>
        )}
        {[...Array(emptyStars)].map((_, i) => (
          <i key={i} className="bx bx-star" style={{ color: "#d1d5db" }}></i>
        ))}
      </div>
    );
  };

  // Handle favorite toggle
  const handleFavoriteToggle = async () => {
    if (updatingFavorite || !contractor?._id) return;

    try {
      setUpdatingFavorite(true);
      const newFavoriteStatus = !isFavorite;
      const contractorId = String(contractor._id);

      const data = await apiFetch(
        `${USER_BASE_URL}/${VERSION}/contractor/add-update-favorites/`,
        "POST",
        JSON.stringify({
          id: contractorId,
          is_favorite: newFavoriteStatus,
        }),
        {
          "Content-Type": "application/json",
        }
      );

      if (data && data.response_code === 200) {
        setIsFavorite(newFavoriteStatus);
      }
    } catch (err) {
      console.error("Error updating favorite:", err);
      showToast(err.message || "Failed to update favorite", "error");
    } finally {
      setUpdatingFavorite(false);
    }
  };

  // Handle view past work details
  const handleViewPastWorkDetails = (work) => {
    setSelectedPastWork(work);
    setCurrentImageIndex(0);
    setShowPastWorkModal(true);
  };

  // Auto-rotate images in modal
  useEffect(() => {
    if (!showPastWorkModal || !selectedPastWork?.photos || selectedPastWork.photos.length <= 1) {
      if (autoRotateIntervalRef.current) {
        clearInterval(autoRotateIntervalRef.current);
        autoRotateIntervalRef.current = null;
      }
      return;
    }

    autoRotateIntervalRef.current = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % selectedPastWork.photos.length);
    }, 3000); // Change image every 3 seconds

    return () => {
      if (autoRotateIntervalRef.current) {
        clearInterval(autoRotateIntervalRef.current);
        autoRotateIntervalRef.current = null;
      }
    };
  }, [showPastWorkModal, selectedPastWork]);

  // Handle touch events for swipe
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!selectedPastWork?.photos || selectedPastWork.photos.length <= 1) return;

    const diff = touchStartX.current - touchEndX.current;
    const minSwipeDistance = 50;

    if (Math.abs(diff) > minSwipeDistance) {
      // Pause auto-rotate temporarily when swiping
      if (autoRotateIntervalRef.current) {
        clearInterval(autoRotateIntervalRef.current);
        autoRotateIntervalRef.current = null;
      }

      if (diff > 0 && currentImageIndex < selectedPastWork.photos.length - 1) {
        // Swipe left - go to next image
        setCurrentImageIndex((prev) => prev + 1);
      } else if (diff < 0 && currentImageIndex > 0) {
        // Swipe right - go to previous image
        setCurrentImageIndex((prev) => prev - 1);
      }

      // Resume auto-rotate after 3 seconds
      setTimeout(() => {
        if (showPastWorkModal && selectedPastWork?.photos && selectedPastWork.photos.length > 1) {
          autoRotateIntervalRef.current = setInterval(() => {
            setCurrentImageIndex((prev) => (prev + 1) % selectedPastWork.photos.length);
          }, 3000);
        }
      }, 3000);
    }
  };

  // Handle document click
  const handleDocumentClick = (url) => {
    if (url) {
      window.open(url, "_blank");
    }
  };

  // Format budget
  const formatBudget = (budget) => {
    if (!budget) return "AED 0";
    return `AED ${budget.toLocaleString("en-IN")}`;
  };

  const formattedCategories = getFormattedCategories();
  const yearsInBusiness = calculateYearsInBusiness();
  const displayCategories = showMoreServices
    ? formattedCategories
    : formattedCategories.slice(0, 3);
  const displayReviews = showMoreReviews ? reviews : reviews.slice(0, 2);

  // Loading screen
  if (loading) {
    return (
      <div className="contractor-details-loading">
        <div className="spinner"></div>
        <p>{t.loading}</p>
      </div>
    );
  }

  // Error screen
  if (error || !contractor) {
    return (
      <div className="contractor-details-container" dir={lang === "ar" ? "rtl" : "ltr"}>
        <Header lang={lang} />
        <div className="contractor-details-page-header">
          <button className="back-button" onClick={() => navigate(-1)}>
            <i className="bx bx-arrow-back"></i>
          </button>
          <h1 className="contractor-details-page-title">{t.contractorDetails}</h1>
          <div className="header-spacer"></div>
        </div>
        <div className="contractor-details-error">
          <img src={noWorkImage} alt="Error" className="error-image" />
          <p className="error-message">{t.noContractorFound}</p>
          <button
            className="go-to-contractors-btn"
            onClick={() => navigate("/find-contractors")}
          >
            {t.goToContractors}
          </button>
        </div>
        <BottomNavbar currentPage="find" lang={lang} />
      </div>
    );
  }

  return (
    <div className="contractor-details-container" dir={lang === "ar" ? "rtl" : "ltr"}>
      <Header lang={lang} />

      {/* Page Header */}
      <div className="contractor-details-page-header">
        <button className="back-button" onClick={() => navigate(-1)}>
          <i className="bx bx-arrow-back"></i>
        </button>
        <h1 className="contractor-details-page-title">{t.contractorDetails}</h1>
        <div className="header-spacer"></div>
      </div>

      <div className="contractor-details-content">
        {/* Basic Details Section */}
        <div className="contractor-details-section">
          <h2 className="section-title">{t.basicDetails}</h2>

          <div className="contractor-basic-details-card">
            <div className="contractor-card-header">
              <button
                className={`contractor-favorite-btn ${isFavorite ? "active" : ""}`}
                onClick={handleFavoriteToggle}
                disabled={updatingFavorite}
                title={isFavorite ? "Remove from favorites" : "Add to favorites"}
              >
                {updatingFavorite ? (
                  <div className="button-spinner-small"></div>
                ) : (
                  <i className={isFavorite ? "bx bxs-heart" : "bx bx-heart"}></i>
                )}
              </button>
            </div>
            {/* Logo */}
            <div className="contractor-logo-wrapper">
              {contractor.profile_image?.url ? (
                <img
                  src={contractor.profile_image.url}
                  alt={contractor.name || "Contractor"}
                  className="contractor-logo-large"
                />
              ) : (
                <div className="contractor-logo-placeholder-large">
                  <i className="bx bx-building"></i>
                </div>
              )}
            </div>

            {/* Name */}
            <h3 className="contractor-name-large">{contractor.name || "N/A"}</h3>

            {/* Start Date */}
            {contractor.company_start_date && (
              <div className="contractor-start-date">
                <i className="bx bx-calendar"></i>
                <span>
                  {t.startDate}: {formatDate(contractor.company_start_date)} (
                  {yearsInBusiness} {t.yearsInBusiness})
                </span>
              </div>
            )}

            {/* Ratings */}
            {contractor.average_rating !== undefined && (
              <div className="contractor-ratings-section">
                <div className="ratings-label">{t.ratings}</div>
                {renderStars(contractor.average_rating)}
                <span className="rating-text">
                  ({contractor.average_rating.toFixed(1)} {t.starRating})
                </span>
              </div>
            )}

            {/* Description */}
            {contractor.description && (
              <div className="contractor-description-section">
                <div className="description-label">{t.description}</div>
                <p
                  className={`contractor-description-text ${
                    showDescriptionFull ? "expanded" : ""
                  }`}
                >
                  {contractor.description}
                </p>
                {contractor.description.length > 150 && (
                  <button
                    className="read-more-btn"
                    onClick={() => setShowDescriptionFull(!showDescriptionFull)}
                  >
                    {showDescriptionFull ? t.readLess : t.readMore}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Services Offered Section */}
        {formattedCategories.length > 0 && (
          <div className="contractor-details-section">
            <h2 className="section-title">{t.servicesOffered}</h2>
            <div className="services-list">
              {displayCategories.map((cat, index) => (
                <span key={index} className="service-chip">
                  {cat}
                </span>
              ))}
            </div>
            {formattedCategories.length > 3 && (
              <button
                className="show-more-btn"
                onClick={() => setShowMoreServices(!showMoreServices)}
              >
                {showMoreServices ? t.showLess : t.showMore}
              </button>
            )}
          </div>
        )}

        {/* Specialisations Section */}
        {getFormattedSpecialisations().length > 0 && (
          <div className="contractor-details-section">
            <h2 className="section-title">{t.specialisations}</h2>
            <div className="services-list">
              {getFormattedSpecialisations().map((spec, index) => (
                <span key={index} className="service-chip">
                  {spec}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Past Works Section */}
        <div className="contractor-details-section">
          <h2 className="section-title">{t.pastWorks}</h2>
          {pastWorks.length === 0 ? (
            <p className="no-data-message">{t.noPastWorks}</p>
          ) : (
            <>
              <div className="past-works-list">
                {pastWorks.map((work) => (
                  <div key={work._id} className="past-work-card-item">
                    <div className="past-work-card-content">
                      {/* Image */}
                      <div className="past-work-image-container">
                        {work.photos && work.photos.length > 0 ? (
                          <div className="past-work-image-wrapper">
                            <img
                              src={work.photos[0].url}
                              alt={work.title || "Work"}
                              className="past-work-image"
                            />
                            {work.photos.length > 1 && (
                              <div className="image-indicators">
                                {work.photos.map((_, index) => (
                                  <span
                                    key={index}
                                    className={`indicator ${
                                      index === 0 ? "active" : ""
                                    }`}
                                  />
                                ))}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="past-work-image-placeholder">
                            <i className="bx bx-image"></i>
                          </div>
                        )}
                      </div>

                      {/* Content */}
                      <div className="past-work-content">
                        <h3 className="past-work-title">
                          {work.sub_categories && work.sub_categories.length > 0
                            ? `${work.sub_categories[0].name} - ${work.sub_categories[0].category_id?.name || ""}`
                            : work.title || "Work"}
                        </h3>
                        <div className="past-work-meta">
                          <div className="past-work-meta-item">
                            <i className="bx bx-file"></i>
                            <span>{formatBudget(work.budget)}</span>
                          </div>
                          <div className="past-work-meta-item">
                            <i className="bx bx-calendar"></i>
                            <span>
                              {work.start_date && formatDate(work.start_date)}
                              {work.completion_date &&
                                ` - ${formatDate(work.completion_date)}`}
                            </span>
                          </div>
                        </div>
                        <div className="past-work-description">
                          <p className="past-work-description-text">
                            {work.description || ""}
                          </p>
                        </div>
                        <button
                          className="view-details-btn"
                          onClick={() => handleViewPastWorkDetails(work)}
                        >
                          {t.viewDetails} &gt;
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Reviews Section */}
        <div className="contractor-details-section">
          <h2 className="section-title">{t.reviews}</h2>
          {reviews.length === 0 ? (
            <p className="no-data-message">{t.noReviews}</p>
          ) : (
            <>
              <div className="reviews-list">
                {displayReviews.map((review) => (
                  <div key={review._id} className="review-card">
                    <div className="review-header">
                      <span className="review-author">
                        {review.user_id?.name || "Anonymous"}
                      </span>
                      <span className="review-date">
                        {formatRelativeTime(review.created_at)}
                      </span>
                    </div>
                    {renderStars(review.star || 0)}
                    <p className="review-text">{review.review || ""}</p>
                  </div>
                ))}
              </div>
              {reviews.length > 2 && (
                <button
                  className="show-more-btn"
                  onClick={() => setShowMoreReviews(!showMoreReviews)}
                >
                  {showMoreReviews ? t.showLess : t.showMore}
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Past Work Modal */}
      {showPastWorkModal && selectedPastWork && (
        <div
          className="past-work-modal-overlay"
          onClick={() => setShowPastWorkModal(false)}
        >
          <div
            className="past-work-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="past-work-modal-header">
              <h2 className="past-work-modal-title">{t.pastWorks}</h2>
              <button
                className="past-work-modal-close"
                onClick={() => setShowPastWorkModal(false)}
              >
                <i className="bx bx-x"></i>
              </button>
            </div>

            <div className="past-work-modal-body">
              {/* Image Carousel */}
              {selectedPastWork.photos && selectedPastWork.photos.length > 0 && (
                <div className="past-work-modal-image-container">
                  <div
                    className="past-work-modal-image-wrapper"
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                  >
                    {selectedPastWork.photos.map((photo, index) => (
                      <img
                        key={photo.id || index}
                        src={photo.url}
                        alt={selectedPastWork.title || "Work"}
                        className={`past-work-modal-image ${
                          index === currentImageIndex ? "active" : ""
                        }`}
                      />
                    ))}
                    {selectedPastWork.photos.length > 1 && (
                      <div className="image-indicators">
                        {selectedPastWork.photos.map((_, index) => (
                          <span
                            key={index}
                            className={`indicator ${
                              index === currentImageIndex ? "active" : ""
                            }`}
                            onClick={() => {
                              // Pause auto-rotate when manually clicking
                              if (autoRotateIntervalRef.current) {
                                clearInterval(autoRotateIntervalRef.current);
                                autoRotateIntervalRef.current = null;
                              }
                              setCurrentImageIndex(index);
                              // Resume after 3 seconds
                              setTimeout(() => {
                                if (showPastWorkModal && selectedPastWork?.photos && selectedPastWork.photos.length > 1) {
                                  autoRotateIntervalRef.current = setInterval(() => {
                                    setCurrentImageIndex((prev) => (prev + 1) % selectedPastWork.photos.length);
                                  }, 3000);
                                }
                              }, 3000);
                            }}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Title */}
              <h3 className="past-work-modal-title-text">
                {selectedPastWork.sub_categories &&
                selectedPastWork.sub_categories.length > 0
                  ? `${selectedPastWork.sub_categories[0].name} - ${selectedPastWork.sub_categories[0].category_id?.name || ""}`
                  : selectedPastWork.title || "Work"}
              </h3>

              {/* Dates and Budget */}
              <div className="past-work-modal-meta">
                {selectedPastWork.start_date && (
                  <div className="past-work-modal-meta-item">
                    <i className="bx bx-calendar"></i>
                    <span>
                      {t.startDate}: {formatDate(selectedPastWork.start_date)}
                    </span>
                  </div>
                )}
                {selectedPastWork.completion_date && (
                  <div className="past-work-modal-meta-item">
                    <i className="bx bx-calendar"></i>
                    <span>
                      {t.endDate}: {formatDate(selectedPastWork.completion_date)}
                    </span>
                  </div>
                )}
                {selectedPastWork.budget && (
                  <div className="past-work-modal-meta-item">
                    <i className="bx bx-briefcase"></i>
                    <span>
                      {t.budget}: {formatBudget(selectedPastWork.budget)}
                    </span>
                  </div>
                )}
              </div>

              {/* Description */}
              {selectedPastWork.description && (
                <div className="past-work-modal-description">
                  <div className="description-label">{t.description}</div>
                  <p className="past-work-modal-description-text">
                    {selectedPastWork.description}
                  </p>
                </div>
              )}

              {/* Documents */}
              {selectedPastWork.documents &&
                selectedPastWork.documents.length > 0 && (
                  <div className="past-work-modal-documents">
                    <div className="description-label">{t.workDocuments}</div>
                    <div className="documents-grid">
                      {selectedPastWork.documents.map((doc, index) => (
                        <div
                          key={doc.id || index}
                          className="document-square-item"
                          onClick={() => handleDocumentClick(doc.url)}
                        >
                          <div className="document-square-icon">
                            <i className="bx bxs-file-pdf"></i>
                          </div>
                          <span className="document-square-name">
                            {doc.id
                              ? doc.id.split("/").pop().substring(0, 15) + "..."
                              : `Doc ${index + 1}`}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
            </div>
          </div>
        </div>
      )}

      <BottomNavbar currentPage="find" lang={lang} />
    </div>
  );
}

